import type { Ref } from 'vue';
import type { BlockSerialized } from '#layers/block-engine/types/block';
import { reactive, watch } from 'vue';
import { createBlockVariables } from '#layers/block-engine/utils/block/variable';

export function useBlockEditorVariables({
	blockData,
}: {
	blockData: Ref<BlockSerialized | null>;
}) {
	/**
	 * Variable values used in the block editor to preview the blocks.
	 */
	const variableValues = reactive<Record<string, any>>({});
	/**
	 * Map of variable id to variable name.
	 */
	const variableIdToName = new Map<string, string>();

	function updateVariables() {
		if (!blockData.value) {
			return;
		}

		const newVars = createBlockVariables(blockData.value);

		for (const key in variableValues) {
			if (!(key in newVars)) {
				delete variableValues[key];
			}
		}

		Object.assign(variableValues, newVars);
		variableIdToName.clear();

		for (const variable of blockData.value.variables ?? []) {
			variableIdToName.set(variable.id, variable.name);
		}
	}

	watch(() => blockData.value?.variables, () => {
		updateVariables();
	}, {
		deep: true,
		immediate: true,
	});

	return {
		variableValues,
		variableIdToName,
	};
}

export type BlockEditorVariables = ReturnType<typeof useBlockEditorVariables>;
